import { useCallback, useLayoutEffect, useState } from 'react';

export interface DimensionObject {
  width: number;
  height: number;
  top: number;
  left: number;
  x: number;
  y: number;
  right: number;
  bottom: number;
}

export type UseDimensionsHook = [(node: HTMLElement | null) => void, DimensionObject];

export interface UseDimensionsArgs {
  liveMeasure?: boolean;
}

function getDimensionObject(node: HTMLElement): DimensionObject {
  const rect = node.getBoundingClientRect();
  const { top } = rect;
  const { left } = rect;
  return {
    width: rect.width,
    height: rect.height,
    top: 'x' in rect ? rect.x : top,
    left: 'y' in rect ? rect.y : left,
    x: 'x' in rect ? rect.x : left,
    y: 'y' in rect ? rect.y : top,
    right: rect.right,
    bottom: rect.bottom,
  };
}

function useDimensions({
  liveMeasure = true,
}: UseDimensionsArgs = {}): UseDimensionsHook {
  const [dimensions, setDimensions] = useState({
    width: 0,
    height: 0,
    top: 0,
    left: 0,
    x: 0,
    y: 0,
    right: 0,
    bottom: 0,
  });
  const [node, setNode] = useState<HTMLElement | null>(null);

  const ref = useCallback((nodeRef: HTMLElement | null) => {
    setNode(nodeRef);
  }, []);

  useLayoutEffect(() => {
    if (node) {
      const measure = () =>
        window.requestAnimationFrame(() => setDimensions(getDimensionObject(node)));
      measure();

      if (liveMeasure) {
        window.addEventListener('resize', measure);

        return () => {
          window.removeEventListener('resize', measure);
        };
      }
    }
  }, [node, liveMeasure]);

  return [ref, dimensions];
}

export default useDimensions;
